import {
  IFormDocument,
  IMListRowgroup,
  ITicket,
} from 'app/services/api5-service/api.interface';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

import { DocHelper } from 'utils/dochelper';

import {
  ChatSummaryGroup,
  ChatSummaryProps,
} from './model/chat-summary-props.interface';

import { ISPFieldConfig, ISPFieldType, ISPFormState } from '../../model';

/**
 * Get summary group
 *
 * @param doc
 * @param group
 */
function getChatSummaryGroup(
  doc: IFormDocument,
  group: IMListRowgroup,
): ChatSummaryGroup {
  return {
    trackBy: doc.$lang,
    titleMsg: DocHelper.getMessage(group.$name, doc),
    items: group.row.map(item => ({
      label: DocHelper.getMessage(item.$name, doc),
      value: item.$,
    })),
  };
}

/**
 * Get chat summary from backend ticket metadata
 *
 * @param doc - document instance
 * @param name - name of list with messages
 */
function getChatSummary(doc: IFormDocument, name: string): ChatSummaryGroup[] {
  const mlist = doc?.mlist?.find(list => list.$name === name);
  const infoMessage = mlist?.message?.find(message => message.$type === 'info');

  if (!infoMessage) {
    return [];
  }

  return (
    infoMessage.rowgroup?.map<ChatSummaryGroup>(group =>
      getChatSummaryGroup(doc, group),
    ) || []
  );
}

/**
 * Get stream of chat summary
 *
 * @param control - control metadata
 * @param state - dynamic form state
 */
function getChatSummary$(
  control: ITicket,
  state: ISPFormState,
): Observable<ChatSummaryGroup[]> {
  return state.docSubject.pipe(
    map(doc => getChatSummary(doc, control.$name)),
    shareReplay({
      refCount: true,
      bufferSize: 1,
    }),
  );
}

/**
 * Get props for chat summary field
 *
 * @param control - control metadata
 * @param state - dynamic form state
 */
export function getChatSummaryProps(
  control: ITicket,
  state: ISPFormState,
): ChatSummaryProps {
  const info = getChatSummary(state.docSubject.value, control.$name);
  const info$ = getChatSummary$(control, state);

  return {
    originalControl: control,
    info$,
    isHidden: !info.length,
    isHidden$: info$.pipe(map(i => !i.length)),
    collapseMsg$: DocHelper.getMessage$('collapse', state.docSubject),
    expandMsg$: DocHelper.getMessage$('expand', state.docSubject),
    layoutPlace: 'summary',
  };
}

/**
 * Get config for chat summary field
 *
 * @param control - control metadata
 * @param state - dynamic form state
 */
export function getChatSummaryConfig(
  control: ITicket,
  state: ISPFormState,
): ISPFieldConfig<ISPFieldType.ChatSummary> {
  const props = getChatSummaryProps(control, state);

  return {
    type: ISPFieldType.ChatSummary,
    props,
  };
}
